/// <reference types="vite-plugin-svgr/client" />
import { SidebarIcon, ShieldCheck } from 'lucide-react';
import { Button, useSidebar } from '@artstorefronts/ui';
import { NavTrigger } from '@artstorefronts/arthelper-nav';

import { useTranslation } from 'react-i18next';

import { useAuth } from '~/context/auth';
import { useAnalytics } from '~/context/analytics';
import UserDropdown from './UserDropdown';
import { ReputationPill } from './reputation/ReputationPill';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';

interface SiteHeaderProps {
  onNavigate?: () => void;
}

const TOWN_SQUARE_URL = __TOWN_SQUARE_URL__ || 'https://www.arthelper.com';

export function SiteHeader({ onNavigate }: SiteHeaderProps) {
  const { toggleSidebar } = useSidebar();
  const { t } = useTranslation('nav');
  const { profile } = useAuth();
  const {
    amplitude: { track },
  } = useAnalytics();

  const { enabled: repEnabled } = useFeatureFlag('reputation_points_ux');

  const isVerifiedProfile = Boolean(profile?.profile?.isHumanVerified);
  const verifyCtaLabel = t(isVerifiedProfile ? 'verified-toolkit' : 'get-verified');

  return (
    <header className="bg-background sticky top-0 z-50 flex w-full shrink-0 items-center border-b">
      <div className="flex w-full items-center gap-2 px-4" style={{ height: '3.5rem' }}>
        {/* Sidebar toggle */}
        <Button className="h-8 w-8" variant="ghost" size="icon" onClick={toggleSidebar}>
          <SidebarIcon />
        </Button>

        {/* The suite bar's restore control, now the first thing after the sidebar toggle:
            the header no longer carries an ArtHelper mark of its own, because the suite
            bar directly above it renders the wordmark. It self-hides while the bar is
            expanded, so while collapsed this is the only way back. The divider that used
            to sit here separated the toggle from that logo and had nothing left to
            divide — the nav's own left margin carries the spacing now. */}
        <NavTrigger />

        {/* Nav links */}
        <nav className="hidden md:flex items-center gap-2 ml-6">
          <Button variant="ghost" size="lg" className="rounded-full text-black font-medium" asChild>
            <a href={`${TOWN_SQUARE_URL}/art`}>{t('art')}</a>
          </Button>
          <Button variant="ghost" size="lg" className="rounded-full text-black font-medium" asChild>
            <a href={`${TOWN_SQUARE_URL}/artists`}>{t('artists')}</a>
          </Button>
          <Button variant="ghost" size="lg" className="rounded-full text-black font-medium" asChild>
            <a href={`${TOWN_SQUARE_URL}/leaderboard`}>{t('leaderboard')}</a>
          </Button>
          <Button variant="ghost" size="lg" className="rounded-full text-black font-medium" asChild>
            <a href={`${TOWN_SQUARE_URL}/guilds`}>{t('guilds')}</a>
          </Button>
        </nav>

        {/* Right side */}
        <div className="ml-auto flex items-center gap-2">
          {profile?.profile && repEnabled && <ReputationPill />}
          {profile?.profile && (
            <a
              href={`${TOWN_SQUARE_URL}/verify`}
              onClick={() =>
                track('verify_external_link_clicked', {
                  source: 'app_header',
                  viewer_state: isVerifiedProfile ? 'verified' : 'unverified',
                })
              }
              aria-label={verifyCtaLabel}
              title={verifyCtaLabel}
              className="inline-flex items-center gap-1.5 whitespace-nowrap px-2.5 xs:px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-bold rounded-full transition-colors"
            >
              <ShieldCheck className="size-4 shrink-0" />
              <span className="hidden xs:inline">{verifyCtaLabel}</span>
            </a>
          )}

          <UserDropdown onNavigate={onNavigate} />
        </div>
      </div>
    </header>
  );
}
